// @ts-nocheck
import React, { useContext, useState } from "react";

import { NavLink, useLocation } from "react-router-dom";
import {
  BellOutlined,
  ReloadOutlined,
  LogoutOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import {
  App,
  Layout,
  Menu,
  Button,
  Badge,
  Drawer,
  FloatButton,
  Modal,
} from "antd";
import ColoredAvatar from "@/components/ColoredAvatar";
import Icon from "@/components/Icon";
import { useAuth } from "@/containers/Auth/AuthContext";
import { WebSocketContextProvider } from "@/components/Data/WebSocketContext";
import { useMentions } from "@/components/MentionsContext";
import { useApp } from "@/containers/Apps/AppContext";
import UserNotifications from "@/components/UserNotifications";
import { DATA_URLS, URLS } from "@/urls";
import { ON_OBJECT_POST_SAVE } from "@/utils/constants";

import CompanySwitcher from "./CompanySwitcher";

import styles from "./index.module.less";
import ServiceMessage, {
  ServiceMessageContext,
} from "@/components/Notifications/ServiceMessage";

const { Header } = Layout;

const HeaderNavigation = ({ backgroundColor }) => {
  const { notification } = App.useApp();
  const location = useLocation();
  const { selectedMembership, reLoginAsSelf, user, loginOwner, membership } =
    useAuth();
  const mentions = useMentions();
  const { config } = useApp();
  const [userNotificationsDrawerOpen, setUserNotificationsDrawerOpen] =
    useState(false);
  const [companySwitcherVisible, setCompanySwitcherVisible] = useState(false);
  const [versionAlertVisible, setVersionAlertVisible] = useState(false);
  const optionalThemeStyles = {};
  const { isServiceMessagePresent } = useContext(ServiceMessageContext);

  let selectedPathName = "";
  let pathNameParts = location.pathname.split("/");

  if (pathNameParts.length >= 2) {
    selectedPathName = `/${pathNameParts[1]}`;
  }

  const performReLoginAsSelf = () => {
    reLoginAsSelf({
      onSuccess: () => {
        document.location.href = URLS.dashboard.index.buildPath();
      },
    });
  };

  const onMentionsWSMessage = (msg) => {
    if (msg.created && msg.object.user === user.id) {
      const audio = new Audio("/notification.mp3");
      // Best-effort notification chime; ignore autoplay/codec rejections
      // (NotAllowedError / NotSupportedError) so they don't spam Sentry.
      audio.play().catch(() => {});

      switch (msg.type) {
        case ON_OBJECT_POST_SAVE:
          const key = `unreadMention${Date.now()}`;

          notification.info({
            key: key,
            message: `${msg.object.mentioned_by__full_name} mentioned you in ${msg.object.content_object__str}`,
            btn: (
              <a
                href={mentions.getHref(msg.object)}
                target="_blank"
                rel="noreferrer noopener"
                className="ant-btn ant-btn-primary"
              >
                View
              </a>
            ),
          });
          break;
        default:
          break;
      }
    }
  };

  const onVersionWSMessage = (msg) => {
    console.info(`App version: ${msg.version}`);
    if (
      config.appVersion &&
      msg.version &&
      config.appVersion !== msg.version &&
      msg.version.indexOf("beta") === -1
    ) {
      setVersionAlertVisible(true);
    } else {
      setVersionAlertVisible(false);
    }
  };

  if (backgroundColor) {
    optionalThemeStyles.backgroundColor = `${backgroundColor}`;
  }

  const isCustomerRole =
    selectedMembership.groupCodename === "ADMIN" ||
    selectedMembership.groupCodename === "USER" ||
    selectedMembership.groupCodename === "GUEST";

  const menuItems = [
    ...(versionAlertVisible
      ? [
          {
            key: "version-alert",
            label: (
              <Button
                icon={<ReloadOutlined />}
                danger
                type="primary"
                onClick={() => {
                  window.location.reload();
                }}
              >
                New version available
              </Button>
            ),
          },
        ]
      : []),
    ...(loginOwner && loginOwner.username
      ? [
          {
            key: "relogin",
            label: (
              <Button
                onClick={performReLoginAsSelf}
                icon={<LogoutOutlined />}
                ghost
              >
                Re-Login as {loginOwner.fullName || loginOwner.username}
              </Button>
            ),
          },
        ]
      : []),
    ...(selectedMembership.groupCodename === "SUPERADMIN"
      ? [
          {
            key: "notifications",
            label: (
              <span>
                <Badge
                  count={mentions.unread && mentions.unread.length}
                  style={{ boxShadow: "none", color: "#fff" }}
                >
                  <BellOutlined style={{ color: "#fff", marginRight: 0 }} />
                </Badge>
              </span>
            ),
            onClick: () => setUserNotificationsDrawerOpen(true),
          },
        ]
      : []),
    ...(isCustomerRole
      ? [
          {
            key: "company-submenu",
            style: { lineHeight: "64px" },
            popupClassName: styles.submenuPopup,
            label: (
              <span className="submenu-title-wrapper">
                <Icon icon="faBuilding" />
                {selectedMembership.companyName}
              </span>
            ),
            children: [
              {
                key: URLS.customerUI.companyProfile.index.buildPath(),
                label: (
                  <NavLink
                    to={URLS.customerUI.companyProfile.index.buildPath()}
                  >
                    Profile
                  </NavLink>
                ),
              },
              ...(membership.length > 1
                ? [
                    {
                      key: "switch-org",
                      label: (
                        <a onClick={() => setCompanySwitcherVisible(true)}>
                          Switch Organisation
                        </a>
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(isCustomerRole
      ? [
          {
            key: "tools-submenu",
            popupClassName: styles.submenuPopup,
            label: (
              <span className="submenu-title-wrapper">
                <ToolOutlined />
                Tools
              </span>
            ),
            children: [
              {
                key: "looking-glass",
                label: (
                  <a
                    href="https://lg.netsg.net.au"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Looking Glass
                  </a>
                ),
              },
              {
                key: "forti-analyzer",
                label: (
                  <a
                    href="https://analyzer.secure-sdwan.net"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    FortiAnalyzer
                  </a>
                ),
              },
              {
                key: "speedtest",
                label: (
                  <a
                    href="https://www.speedtest.net/server/30430"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Speed Test
                  </a>
                ),
              },
            ],
          },
        ]
      : []),
    {
      key: "user-submenu",
      style: { lineHeight: "64px" },
      theme: "light",
      popupClassName: styles.submenuPopup,
      label: (
        <span className="submenu-title-wrapper">
          <ColoredAvatar value={user.fullName} showTooltip={false} />
        </span>
      ),
      children: [
        ...(selectedMembership.groupCodename === "SUPERADMIN" &&
        membership.length > 1
          ? [
              {
                key: "superadmin-switch-org",
                label: (
                  <a onClick={() => setCompanySwitcherVisible(true)}>
                    Switch Organisation
                  </a>
                ),
              },
            ]
          : []),
        {
          key: "user-info",
          className: styles.userInfoMenuItem,
          label: (
            <>
              <div className={styles.fullNameWrapper}>{user.fullName}</div>
              <div className={styles.permissionWrapper}>
                {selectedMembership.groupCodename}
              </div>
            </>
          ),
        },
        {
          key: URLS.account.logout.buildPath(),
          label: <NavLink to={URLS.account.logout.buildPath()}>Logout</NavLink>,
        },
      ],
    },
  ];

  return (
    <>
      <Drawer
        id="id-user-notifications-drawer"
        open={userNotificationsDrawerOpen}
        title="Notifications"
        destroyOnHidden
        closable
        placement="right"
        width={500}
        onClose={() => setUserNotificationsDrawerOpen(false)}
      >
        <UserNotifications />
        <FloatButton.BackTop
          style={{ right: 40 }}
          target={() =>
            document
              .getElementById("id-user-notifications-drawer")
              .getElementsByClassName("ant-drawer-body")[0]
          }
        />
      </Drawer>
      {companySwitcherVisible && (
        <Modal
          title="Switch Organisation"
          open
          closable
          onCancel={() => setCompanySwitcherVisible(false)}
          width={800}
          footer={[
            <Button onClick={() => setCompanySwitcherVisible(false)}>
              Cancel
            </Button>,
          ]}
        >
          <CompanySwitcher />
        </Modal>
      )}
      <ServiceMessage />
      <Header
        className="header"
        style={{
          position: "fixed",
          top: isServiceMessagePresent ? "40px" : "0px",
          zIndex: 100,
          width: "100%",
          ...optionalThemeStyles,
        }}
      >
        <div className="logo" style={{ ...optionalThemeStyles }} />

        <WebSocketContextProvider
          url={DATA_URLS.generic.ws.version.buildPath()}
          onMessage={onVersionWSMessage}
        >
          <WebSocketContextProvider
            url={
              selectedMembership.groupCodename === "SUPERADMIN"
                ? DATA_URLS.internal.mentions.ws.unread.buildPath()
                : null
            }
            onMessage={onMentionsWSMessage}
          >
            <div className="top-navigation">
              <Menu
                mode="horizontal"
                theme="dark"
                disabledOverflow
                style={{ float: "right", ...optionalThemeStyles }}
                selectedKeys={[selectedPathName]}
                items={menuItems}
              />
            </div>
          </WebSocketContextProvider>
        </WebSocketContextProvider>
      </Header>
    </>
  );
};

export default HeaderNavigation;
